import { Heading } from '@mm/components';
import { MessengerAdIcon } from '@mm/icons/CampaignIcons';
import { CtmAnalyticsModel } from '@mm/state/model/CtmAnalyticsModel';
import { activeChatbotIdSelector } from '@mm/state/selectors/viewSelectors';
import { ReactComponent as Next } from 'assets/icons/next.svg';
import { ReactComponent as Previous } from 'assets/icons/previous.svg';
import classNames from 'classnames';
import React from 'react';
import { useSelector } from 'react-redux';
import { useContextSelector } from 'use-context-selector';
import useRouter from 'use-react-router';
import {
  Grid,
  GridCell,
  GridRow,
} from '../../../../../../../common/components/Grid/Grid';
import { TooltipContainer } from '../../../../../../../common/components/TooltipContainer/TooltipContainer';
import { CHATBOT_EDITOR } from '../../../../../../../common/constants/routes';
import {
  currencySubCode,
  pureSymbolForCurrency,
} from '../../../../../../common/utils';
import { LiveChatContext } from '../../../../../liveChat/LiveChatContext';
import './AdsAnalyticsTable.pcss';

const CTM_VARIABLE_ID = 18;
const SM_VARIABLE_ID = 22;

type Props = {
  currencyCode: string;
  records: {
    data: CtmAnalyticsModel[];
    next: string | null;
    prev: string | null;
  };
  onChangePage: (token: string | null) => void;
};

const tooltipCSSAttributes = {
  classNamePopoverContainer: 'ads-analytics-table__tooltip-popover',
  classNameContainer: 'ads-analytics-table__tooltip-container',
  classNameContent: 'ads-analytics-table__tooltip-content',
  classNameAnchor: 'ads-analytics-table__tooltip-anchor',
  classNameIconAnchor: 'fas fa-exclamation-circle',
  position: 'top',
} as const;

export const AdsAnalyticsTable = (props: Props) => {
  const {
    records: { data, next, prev },
    onChangePage,
    currencyCode,
  } = props;
  const currencySymbol = pureSymbolForCurrency(currencyCode);
  const subCode = currencySubCode(currencyCode) || currencyCode;
  const prevPageClasses = classNames('ads-analytics-table__page-link', {
    'ads-analytics-table__page-link--disabled': prev === null,
  });
  const nextPageClasses = classNames('ads-analytics-table__page-link', {
    'ads-analytics-table__page-link--disabled': next === null,
  });
  const { history } = useRouter();
  const chatbotId = useSelector(activeChatbotIdSelector);

  const lcService = useContextSelector(
    LiveChatContext,
    v => v.lcServiceRef?.current,
  );

  const lcMachineSend = lcService?.send;

  const action = async (isSm: boolean, adId: string) => {
    const viewUpdate = {
      selectedTags: [],
      selectedAttribute: {
        variableType: 'SystemVariable' as const,
        value: [adId],
        operator: 'equal' as const,
        variableId: isSm ? SM_VARIABLE_ID : CTM_VARIABLE_ID,
      },
    };

    lcMachineSend?.({
      type: 'LC_VIEW_UPDATE',
      payload: { viewUpdate },
    });

    history.push(`${CHATBOT_EDITOR}/${chatbotId}/live-chat`);
  };
  if (data.length === 0) {
    return (
      <div className="ads-analytics-table__empty-state">
        <div className="ads-analytics-table__empty-state-icon-container">
          <MessengerAdIcon
            className="ads-analytics-table__empty-state-icon"
            color="#e7e7e9"
          />
        </div>
        <Heading
          size={6}
          className="ads-analytics-table__empty-state-text"
          text="No ads were found for the specified date range"
        />
      </div>
    );
  }
  const tooltipedCell = (
    tooltip: string,
    content: React.ReactNode,
    align?: 'center' | 'start' | 'end',
  ) => (
    <>
      <div className="ads-analytics-table__tooltip-heading-content">
        {content}
        <TooltipContainer
          {...tooltipCSSAttributes}
          classNameIconAnchor="far fa-question-circle"
          message={tooltip}
          align={align}
        />
      </div>
    </>
  );
  return (
    <div className="ads-analytics-table__container">
      <div className="ads-analytics-table__heading">Ads Analytics</div>
      <Grid className="ads-analytics-table__grid">
        <GridRow className="ads-analytics-table__grid-heading-row">
          <GridCell fr={1.5}>
            <span>Campaign</span>
          </GridCell>
          <GridCell fr={1.5}>
            <span>Ad Set</span>
          </GridCell>
          <GridCell fr={1}>
            <span>Ad</span>
          </GridCell>
          <GridCell fr={1.5}>
            {tooltipedCell(
              'Number of conversations tracked in MM inbox',
              <span>Tracked Conversations</span>,
            )}
          </GridCell>
          <GridCell fr={1.5}>
            <span>Impressions</span>
          </GridCell>
          <GridCell fr={1}>
            {tooltipedCell(
              'Number of people who saw your ad',
              <span>Reach</span>,
            )}
          </GridCell>
          <GridCell fr={1.5}>
            <span>CPM ({subCode})</span>
          </GridCell>
          <GridCell fr={1.5}>
            <span>Spend ({subCode})</span>
          </GridCell>
          <GridCell fr={1.5}>
            {tooltipedCell(
              'Number of conversations reported on FB',
              <span>Conversations</span>,
            )}
          </GridCell>
          <GridCell fr={2}>
            {tooltipedCell(
              'Cost per conversation reported on FB',
              <span>Cost per conversation ({subCode})</span>,
            )}
          </GridCell>
          <GridCell fr={1}>
            {tooltipedCell(
              'Number of clicks on the ad that led to Messenger',
              <span>Link Clicks</span>,
            )}
          </GridCell>
          <GridCell fr={1.5}>
            {tooltipedCell(
              'Number of people who performed a link click',
              <span>Unique Link Clicks</span>,
            )}
          </GridCell>
          <GridCell fr={1}>
            {tooltipedCell(
              'Percentage of times people saw your ad & performed a link click',
              <span>CTR</span>,
              'end',
            )}
          </GridCell>
          <GridCell fr={1}>
            {tooltipedCell(
              'Percentage of people who saw your ad & performed a link click',
              <span>Unique CTR</span>,
              'end',
            )}
          </GridCell>
        </GridRow>
        {data.map((insights: CtmAnalyticsModel, index: number) => (
          <GridRow key={index} className="ads-analytics-table__grid-row">
            <GridCell fr={1.5}>{insights.campaignName}</GridCell>
            <GridCell fr={1.5}>{insights.adsetName}</GridCell>
            <GridCell fr={1}>{insights.adName}</GridCell>
            <GridCell
              className="ads-analytics-table__link-grid-cell"
              fr={1.5}
              onClick={() => action(insights.smAd, insights.adId)}
            >
              {insights.trackedConversations || 0}
            </GridCell>
            <GridCell fr={1.5}>{insights.impressions}</GridCell>
            <GridCell fr={1}>{insights.reach}</GridCell>
            <GridCell fr={1.5}>
              {currencySymbol}
              {Number(insights.cpm).toFixed(2)}
            </GridCell>
            <GridCell fr={1.5}>
              {currencySymbol}
              {Number(insights.spend).toFixed(2)}
            </GridCell>
            <GridCell fr={1.5}>{insights.conversations || 0}</GridCell>
            <GridCell fr={2}>
              {currencySymbol}
              {Number(insights.costPerConversation || 0).toFixed(2)}
            </GridCell>
            <GridCell fr={1}>{insights.inlineLinkClicks || 0}</GridCell>
            <GridCell fr={1.5}>{insights.uniqueInlineLinkClicks || 0}</GridCell>
            <GridCell fr={1}>
              {Number(insights.inlineLinkClickCtr || 0).toFixed(1)}%
            </GridCell>
            <GridCell fr={1}>
              {Number(insights.uniqueInlineLinkClickCtr || 0).toFixed(1)}%
            </GridCell>
          </GridRow>
        ))}
      </Grid>
      <div className="ads-analytics-table__pagination-controls">
        <div
          className={prevPageClasses}
          onClick={() => prev !== null && onChangePage(prev)}
        >
          <Previous className="ads-analytics-table__page-link-previous" />
          <span>Previous</span>
        </div>
        <div
          className={nextPageClasses}
          onClick={() => next !== null && onChangePage(next)}
        >
          <span>Next</span>
          <Next className="ads-analytics-table__page-link-next" />
        </div>
      </div>
    </div>
  );
};
